import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteServiceOfferings,
  getServiceOfferingsList,
  saveServiceOfferings,
} from "../../api";
import { setLoading } from "../../redux/loadingSlice";
import { setNotification } from "../../redux/notificationSlice";
import { AddRecord, DeleteRecord, EditRecord, Pagination } from "../core";
import { ConfirmationModal } from "../modals/ConfirmationModal";
import { UpdateModal } from "../modals/UpdateModal";

export const ServiceOfferingList = () => {
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editService, setEditService] = useState({ id: "", name: "" });
  const [deleteServiceModal, setDeleteServiceModal] = useState(false);
  const [serviceOfferingData, setServiceOfferingData] = useState([]);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(serviceOfferingData.length / itemsPerPage);
  const currentItems = serviceOfferingData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const userInfo = useSelector((state) => state.user?.profile);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setLoading(false));
    const fetchAdminData = async () => {
      try {
        const reqData = { operation: "fetchServiceOffering" };
        const serviceOfferingsResp = await getServiceOfferingsList(reqData);
        setServiceOfferingData(serviceOfferingsResp);
        dispatch(setLoading(true));
      } catch (error) {
        console.error(error);
        dispatch(
          setNotification({
            type: "warning",
            show: true,
            message: "Error fetching service offerings. Please try again",
          })
        );
      }
    };
    fetchAdminData();
  }, [setServiceOfferingData, dispatch]);

  const openServiceModal = (data = { id: null, name: "" }) => {
    setEditService(data);
    setShowServiceModal(true);
  };

  const handleServiceSave = async (data) => {
    let updatedData;
    if (editService.id) {
      updatedData = {
        operation: "updateServiceOffering",
        request: { id: editService.id, ...data, username: userInfo.isid },
      };
    } else {
      updatedData = {
        operation: "createServiceOffering",
        request: { ...data, username: userInfo.isid },
      };
    }
    const resp = await saveServiceOfferings(updatedData);
    if (resp?.responseCode === 200) {
      if (updatedData.operation === "updateServiceOffering") {
        setServiceOfferingData(
          serviceOfferingData.map((item) =>
            item.id === updatedData.request.id ? updatedData.request : item
          )
        );
        dispatch(
          setNotification({
            type: "success",
            show: true,
            message: "Success! Record updated successfully",
          })
        );
      } else {
        setServiceOfferingData([...serviceOfferingData, data]);
        dispatch(
          setNotification({
            type: "success",
            show: true,
            message: "Success! Record added successfully",
          })
        );
      }
    } else if (resp?.responseCode === 400) {
      dispatch(
        setNotification({
          type: "warning",
          show: true,
          message: "Record already exist.",
        })
      );
    } else {
      dispatch(
        setNotification({
          type: "error",
          show: true,
          message: "Error! Something went wrong. Please try again",
        })
      );
    }
  };

  const openServiceDeleteModal = (data) => {
    setDeleteServiceModal(true);
    setRowToDelete(data);
  };

  const deleteServiceRow = async () => {
    const dataToDelete = {
      operation: "removeServiceOffering",
      request: { id: rowToDelete.id },
    };
    const resp = await deleteServiceOfferings(dataToDelete);
    if (resp.responseCode === 200) {
      setServiceOfferingData(
        serviceOfferingData.filter((item) => item.id !== rowToDelete.id)
      );
      setDeleteServiceModal(false);
      setRowToDelete(null);
      dispatch(
        setNotification({
          type: "success",
          show: true,
          message: "Success! Record deleted successfully",
        })
      );
    } else {
      dispatch(
        setNotification({
          type: "error",
          show: true,
          message: "Error! Something went wrong. Please try again",
        })
      );
    }
  };

  const closeServiceModal = () => {
    setShowServiceModal(false);
  };

  return (
    <>
      <button
        className="bg-sky-800 text-white px-2 py-px float-right mb-2"
        onClick={() => openServiceModal()}
      >
        <AddRecord />
        Add New
      </button>
      <table className="table-auto border-collapse border border-slate-400 min-w-full">
        <thead className="bg-gray-300 text-left">
          <tr className="border border-slate-300">
            <th className="p-2">Service Offerings</th>
            {userInfo && userInfo.role !== "Read" ? (
              <th className="p-2"></th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {currentItems?.map((row) => (
            <tr
              key={`${row.name}-${row.id}`}
              className="border border-slate-300"
            >
              <td className="px-4 py-2">{row.name}</td>
              {userInfo && userInfo.role !== "Read" ? (
                <td className="p-2 text-right">
                  <button
                    className="text-white px-2 py-px"
                    value={row.id}
                    data-testid="edit"
                    onClick={() => openServiceModal(row)}
                  >
                    <EditRecord />
                  </button>
                  <button
                    className="text-white px-2 py-px ml-2"
                    data-testid="delete"
                    onClick={() => openServiceDeleteModal(row)}
                  >
                    <DeleteRecord />
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {serviceOfferingData.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <UpdateModal
        isOpen={showServiceModal}
        header={"Add/Edit Service"}
        fields={[{ name: "name", label: "Service Name" }]}
        initialData={editService}
        onClose={closeServiceModal}
        onSubmit={handleServiceSave}
      />
      <ConfirmationModal
        isOpen={deleteServiceModal}
        onClose={() => setDeleteServiceModal(false)}
        onDelete={() => deleteServiceRow()}
        header="Confirm delete Service Offerings"
        message="Are you sure you want to delete? This action cannot be undone."
      />
    </>
  );
};
